/**
 * Owns the Bookings table data for the Resourcing tab.
 *
 * The table is paged server-side: every page change, page-size change, search,
 * filter or sort issues a fresh request for just that page instead of loading
 * every booking on the project up front.
 */

import { useEffect, useMemo, useState } from 'react';

import type { ColumnSort } from '@/components/ui/data-table-enhanced';
import { calculatePagination, type PaginationMetadata } from '@/types/pagination';
import type { BookingStatus, SchedulingBooking } from '@/lib/mock-scheduling-data';
import { fetchSchedulingBookings } from '@/services/schedulingService';

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

export interface BookingsPageResult {
  bookings: SchedulingBooking[];
  pagination: PaginationMetadata;
  roleOptions: string[];
  resourceOptions: string[];
  loading: boolean;
  error: string | null;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  search: string;
  setSearch: (search: string) => void;
  statusFilter: BookingStatus | 'all';
  setStatusFilter: (status: BookingStatus | 'all') => void;
  roleFilter: string;
  setRoleFilter: (roleName: string) => void;
  resourceFilter: string;
  setResourceFilter: (resourceName: string) => void;
  /** Bookings ending on or after this date (yyyy-MM-dd), or '' for no lower bound */
  dateFrom: string;
  setDateFrom: (date: string) => void;
  /** Bookings starting on or before this date (yyyy-MM-dd), or '' for no upper bound */
  dateTo: string;
  setDateTo: (date: string) => void;
  columnSorts: ColumnSort[];
  setColumnSorts: (sorts: ColumnSort[]) => void;
}

export function useBookingsPage(projectId: string | undefined, enabled: boolean): BookingsPageResult {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BookingStatus | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [resourceFilter, setResourceFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([{ columnId: 'startDate', direction: 'asc' }]);

  const [bookings, setBookings] = useState<SchedulingBooking[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [resourceOptions, setResourceOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const sort = columnSorts[0];

  useEffect(() => {
    if (!enabled || !projectId) return;
    let cancelled = false;

    async function loadPage() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchSchedulingBookings(projectId!, {
          page,
          pageSize,
          search: debouncedSearch,
          status: statusFilter,
          roleName: roleFilter,
          resourceName: resourceFilter,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          sortBy: sort?.columnId,
          sortDirection: sort?.direction,
        });
        if (cancelled) return;
        setBookings(response.bookings);
        setTotalItems(response.pagination?.totalItems ?? response.bookings.length);
        // Role and resource options span every booking, so only replace them when the server sends them
        if (response.roleOptions && response.roleOptions.length > 0) setRoleOptions(response.roleOptions);
        if (response.resourceOptions && response.resourceOptions.length > 0) setResourceOptions(response.resourceOptions);
      } catch (err) {
        if (cancelled) return;
        console.error('[ResourcingTab] Failed to fetch bookings page:', err);
        setError('Unable to load bookings.');
        setBookings([]);
        setTotalItems(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPage();
    return () => { cancelled = true; };
  }, [enabled, projectId, page, pageSize, debouncedSearch, statusFilter, roleFilter, resourceFilter, dateFrom, dateTo, sort?.columnId, sort?.direction]);

  const pagination = useMemo<PaginationMetadata>(
    () => calculatePagination(totalItems, page, pageSize),
    [totalItems, page, pageSize],
  );

  return {
    bookings,
    pagination,
    roleOptions,
    resourceOptions,
    loading,
    error,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    roleFilter,
    setRoleFilter,
    resourceFilter,
    setResourceFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    columnSorts,
    setColumnSorts,
  };
}
